import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Home, Loader2, RefreshCw, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleProvider";
import api from "@/lib/api";
import { aed } from "@/lib/format";
import { cn } from "@/lib/utils";
import { resolveOpsStatus } from "@/lib/tableFloorUi";
import "@/styles/simple-ui.css";

const FILTERS = [
  { id: "All", labelKey: "simple.posAll" },
  { id: "available", labelKey: "simple.tablesAvailable" },
  { id: "occupied", labelKey: "simple.tablesBusy" },
  { id: "waitingFood", labelKey: "simple.tablesKitchen" },
  { id: "ready", labelKey: "simple.tablesReady" },
  { id: "billing", labelKey: "simple.tablesBill" },
];

const TILE_COLORS = {
  available: "bg-emerald-500",
  occupied: "bg-blue-600",
  waitingFood: "bg-orange-500",
  ready: "bg-violet-600",
  billing: "bg-rose-600",
  reserved: "bg-purple-500",
  multi: "bg-indigo-600",
};

export default function SimpleTables() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const navigate = useNavigate();
  const { user } = useAuth();
  const branchId = user?.default_branch?.id;
  const counterId = user?.default_counter?.id;
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["table-floor", branchId, search.trim()],
    queryFn: async () => {
      const params = { branch_id: branchId };
      if (search.trim()) params.search = search.trim();
      const { data: res } = await api.get("/tables/floor", { params });
      return res;
    },
    enabled: Boolean(branchId),
    refetchInterval: 10000,
  });

  const tables = data?.tables || [];

  const visible = useMemo(() => {
    return tables.filter((table) => {
      const ops = resolveOpsStatus(table);
      if (filter !== "All" && ops.key !== filter) return false;
      return true;
    });
  }, [tables, filter]);

  function openTable(table) {
    if (!counterId) {
      toast.error(t("pos.selectBranchCounter", "Select a counter first"));
      return;
    }
    if (table.status === "Vacant") {
      navigate("/pos", {
        state: {
          openTable: {
            restaurant_table_id: table.id,
            table_code: table.code,
            table_name: table.name,
          },
        },
      });
      return;
    }
    const bill = table.active_bills?.[0];
    if (!bill) {
      toast.error(t("simple.tablesNoOrder", "No active order on this table"));
      return;
    }
    navigate("/pos", {
      state: {
        openBillId: bill.id,
        recallIfHeld: bill.status === "held",
        tableCode: table.code,
      },
    });
  }

  if (!branchId) {
    return (
      <div className="grid h-full place-items-center p-6 text-slate-500">
        {t("pos.selectBranchCounter", "Select a branch first")}
      </div>
    );
  }

  return (
    <div className="simple-ui flex h-full flex-col" dir={isRtl ? "rtl" : "ltr"} data-testid="simple-tables">
      <div className="flex flex-wrap items-center gap-2 border-b bg-white px-3 py-2">
        <Link to="/" className="inline-flex h-10 items-center gap-1 rounded-xl bg-violet-50 px-3 text-sm font-bold text-violet-700">
          <Home className="h-4 w-4" />
          {t("common.home")}
        </Link>
        <h1 className="flex-1 text-lg font-extrabold text-slate-900">{t("nav.tableManagement")}</h1>
        <button
          type="button"
          onClick={() => refetch()}
          className="grid h-10 w-10 place-items-center rounded-xl bg-slate-100"
          disabled={isFetching}
        >
          <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
        </button>
      </div>

      <div className="space-y-2 border-b bg-white p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("common.search")}
            className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 ps-9 pe-3 text-sm outline-none focus:border-violet-400"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={cn(
                "shrink-0 rounded-full px-3 py-1.5 text-xs font-bold",
                filter === f.id ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600",
              )}
            >
              {t(f.labelKey, f.id)}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {isLoading ? (
          <div className="grid place-items-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">{t("simple.tablesEmpty", "No tables")}</div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {visible.map((table) => {
              const ops = resolveOpsStatus(table);
              const bill = table.active_bills?.[0];
              return (
                <button
                  key={table.id}
                  type="button"
                  onClick={() => openTable(table)}
                  className={cn(
                    "simple-tile flex min-h-[130px] flex-col items-center justify-center gap-1 p-4 text-center text-white",
                    TILE_COLORS[ops.key] || TILE_COLORS.occupied,
                  )}
                  data-testid={`simple-table-${table.id}`}
                >
                  <div className="text-2xl font-black">{table.code || table.name}</div>
                  <div className="text-sm font-bold opacity-95">{ops.label}</div>
                  <div className="mt-1 flex items-center gap-1 text-xs font-semibold opacity-90">
                    <Users className="h-3.5 w-3.5" />
                    {table.capacity || "—"}
                  </div>
                  {bill ? (
                    <div className="mt-1 font-mono text-sm font-extrabold">{aed(bill.net_amount)}</div>
                  ) : (
                    <div className="mt-1 text-xs font-semibold opacity-80">{t("simple.tablesTapOpen", "Tap to open")}</div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
